import { useCallback, useMemo } from 'react';
import type { DeselectOptionActionMeta, InputActionMeta, SelectOptionActionMeta } from 'react-select';

import type { Nullable } from '../../../models';
import MenuList from '../../../pages/Trafficking/TableFilterBar/MenuList';
import type { AdFormValues, TargetingValueOption } from '../../AdForm/adFormik';
import { SelectInputAction } from '../../constants/select';
import type { DropdownOption } from '../../Dropdown';
import { commonCustomComponents } from '../../Dropdown/Dropdown';
import { useFieldFast } from '../../Form/hooks';
import MultiSelectDropdown from '../../MultiSelectDropdown';
import type { TargetingScope } from '../../Targeting';
import { useTargetingValuesDropdown } from '../hooks';
import type { UseTargetingValuesDropdownReturn } from '../hooks/useTargetingValuesDropdown';
import type { MultiSelectTargetingValue } from '../types';
import { getEmptyTextMessage, getNoOptionsMessage, getSupportedValuesOptions } from '../utils';

export type TargetingValuesDropdownProps = {
  onClose: () => void;
  handleSelect: (
    options: MultiSelectTargetingValue,
    meta: SelectOptionActionMeta<DropdownOption> | DeselectOptionActionMeta<DropdownOption>
  ) => void;
  handleSelectAll: (options: MultiSelectTargetingValue) => void;
  handleClearAll: () => void;
  selectedValues: TargetingValueOption[];
  selectedDimension: Nullable<DropdownOption>;
  readonly?: boolean;
  errMsg?: string;
  dataTestId?: string;
  /**
   * Read-only configuration describing the entity / entities being targeted.
   * See `TargetingScope` for field-by-field documentation. The values dropdown
   * reads `countryIdsForFilteringValues`, `publishersForFilteringValues`,
   * `isAdEntity`, and `adTypes`.
   */
  scope: TargetingScope;
};

/**
 * Dropdown component for selecting targeting values within a dimension.
 * Supports infinite scroll pagination and search functionality.
 */
const TargetingValuesDropdown = ({
  readonly,
  onClose,
  handleSelect,
  handleSelectAll,
  handleClearAll,
  selectedValues,
  selectedDimension,
  errMsg,
  dataTestId,
  scope,
}: TargetingValuesDropdownProps): JSX.Element => {
  const { isAdEntity, adTypes, countryIdsForFilteringValues, publishersForFilteringValues } = scope;

  const [, { value: newTargeting }] = useFieldFast<AdFormValues['newTargeting']>('newTargeting');

  const {
    options,
    error,
    loading,
    loadingMore,
    search,
    searchTerm,
    clearSearch,
    onNext,
    hasMore,
    filterOption,
    supportEmptySearch,
    searching,
  }: UseTargetingValuesDropdownReturn = useTargetingValuesDropdown(
    selectedDimension?.id,
    newTargeting,
    countryIdsForFilteringValues,
    publishersForFilteringValues
  );

  const filteredValuesOptions = useMemo(
    () =>
      getSupportedValuesOptions({
        dropdownOptions: options,
        lineItemPublishers: publishersForFilteringValues,
        isAdEntity,
        selectedDimension,
        adTypes,
      }),
    [options, publishersForFilteringValues, isAdEntity, selectedDimension, adTypes]
  );

  const components = useMemo(() => ({ ...commonCustomComponents, MenuList }), []);

  const handleSearchValues = useCallback(
    (value: string, { action }: InputActionMeta): void => {
      if (action === SelectInputAction.InputChange) search(value);
    },
    [search]
  );

  const handleClose = useCallback((): void => {
    onClose();
    // Use clearSearch instead of reset: if no search was active the cached
    // pages survive the close so re-opening is instant.  If a search was
    // active, clearSearch resets data and refetches the unfiltered list.
    clearSearch();
  }, [onClose, clearSearch]);

  const handleSelectAllOptions = useCallback((): void => {
    handleSelectAll(filteredValuesOptions);
  }, [handleSelectAll, filteredValuesOptions]);

  const handleChange = useCallback(
    (
      _selectedOption: unknown,
      meta: SelectOptionActionMeta<DropdownOption> | DeselectOptionActionMeta<DropdownOption>
    ): void => {
      handleSelect(filteredValuesOptions, meta);
    },
    [handleSelect, filteredValuesOptions]
  );

  // Don't disable the dropdown while a search fetch is in flight — the user
  // must be able to keep typing.  Only treat loading as disabling when there
  // is no active search term (i.e. the very first page load).
  const isDisabled = !selectedDimension || (loading && !searchTerm) || readonly;

  // When the initial load completes with zero options, keep the dropdown
  // openable so the user can see "No Options", but hide the search input —
  // searching over zero entries will always return zero.
  const noOptionsAfterLoad =
    !loading && !loadingMore && filteredValuesOptions.length === 0 && !searchTerm && supportEmptySearch && !error;

  const noOptionsMessage = useCallback((): string => getNoOptionsMessage(searching, supportEmptySearch, searchTerm), [
    searching,
    supportEmptySearch,
    searchTerm,
  ]);

  const emptyDisplayText = useMemo(() => getEmptyTextMessage(loading, selectedDimension), [loading, selectedDimension]);

  const placeholder = supportEmptySearch ? 'Search' : 'Search to find options';

  return (
    <MultiSelectDropdown<string>
      label="Value"
      value={selectedValues}
      options={filteredValuesOptions}
      loading={loading}
      loadingMore={loadingMore}
      error={error}
      isDisabled={isDisabled}
      isSearchable={!noOptionsAfterLoad}
      filterOption={filterOption}
      onInputChange={handleSearchValues}
      multipleSelectControls={true}
      handleSelectAll={handleSelectAllOptions}
      handleClearAll={handleClearAll}
      inputValue={searchTerm}
      onNext={onNext}
      components={components}
      hasMore={hasMore}
      onClose={handleClose}
      emptyDisplayText={emptyDisplayText}
      maxMenuHeight={400}
      onChange={handleChange}
      noOptionsMessage={noOptionsMessage}
      errMsg={errMsg}
      dataTestId={dataTestId}
      placeholder={placeholder}
      showSearchOverlay
    />
  );
};

export default TargetingValuesDropdown;
